import { useFormik } from 'formik';
import { pick } from 'lodash-es';
import { ChangeEvent, useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetBusinessTypesQuery, useGetIndustriesQuery } from '../../../../api/reference-data';
import { businessInfoOneValidationSchema } from '../../../../config/page-validation-schema';
import { yearsOfOperationOptions } from '../../../../domain/fleet';
import { FormikAutoCompleteField } from '../../../../general/inputs/formik-auto-complete-field';
import { FormikEinField } from '../../../../general/inputs/formik-ein-field';
import { FormikSelectField } from '../../../../general/inputs/formik-select-field';
import { FormikTextField } from '../../../../general/inputs/formik-text-field';
import { RootState } from '../../../../store';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { businessPageOneKeys, BusinessPageOnePayload, saveBusinessInfoOne } from '../../reducers/business-info';

const selector = (state: RootState) => {
  const {
    businessInfo: { info },
    nextStep: { loading },
  } = state;
  return {
    saving: loading,
    businessInfo: pick(info, businessPageOneKeys),
  };
};

const validationSchema = businessInfoOneValidationSchema;

const BusinessInfoOneForm: FC = () => {
  const { data: industries } = useGetIndustriesQuery();
  const { data: businessTypes } = useGetBusinessTypesQuery();

  const dispatch = useDispatch();
  const { businessInfo, saving } = useSelector(selector);

  const formik = useFormik({
    initialValues: {
      ...businessInfo,
    },
    validationSchema,
    onSubmit: (values: BusinessPageOnePayload) => {
      dispatch(saveBusinessInfoOne({ ...values, name: values.name.trim() }));
    },
    enableReinitialize: true,
  });

  const formikSetFieldValue = formik.setFieldValue;
  const onChangeEin = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const ein = e.target.value.replace(/\D/g, '');
      void formikSetFieldValue('ein', ein);
    },
    [formikSetFieldValue],
  );

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField formik={formik} id="name" name="name" label="Company Legal Name" data-public />
        <FormikSelectField
          formik={formik}
          name="yearsOfOperation"
          id="yearsOfOperation"
          label="Years of Operation"
          options={yearsOfOperationOptions}
        />
        <FormikEinField name="ein" id="ein" label="Company EIN" formik={formik} onChange={onChangeEin} />
        <FormikSelectField
          formik={formik}
          name="businessType"
          id="businessType"
          options={businessTypes ?? []}
          label="Business Type"
          placeholder="Business Type"
          data-public
        />
        <FormikAutoCompleteField
          formik={formik}
          name="industryId"
          id="industryId"
          options={industries ?? []}
          label="Industry"
          autoHighlight
          autoSelect
          data-public
        />

        <SubmitWorkflowButton type="submit" progress={saving} sx={{ mt: 3 }}>
          Next
        </SubmitWorkflowButton>
      </form>
    </>
  );
};

export { BusinessInfoOneForm };
